import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  handleChangeSSRDetail,
} from '../store/slices/essSwitchSlice';
import { selectDescription } from '../store/slices/ssrDescriptionSlice';

import styled, { css } from 'styled-components';
import { layerA180Deg, layerADisabled180Deg } from '../styles/commonStyles';

import SSRDetailButtonContainer from './SSRDetailButtonContainer';
import HeaterRow from './HeaterRow';
import ConfirmSummary from './ConfirmSummary';
import { useHeaterEditor } from './useHeaterEditor';
import { removeDeletedSpecs } from './heaterChanges';
import { MODES } from './heaterEditorReducer';
import {
  SSR_CURRENT_CAPACITY_A,
  exceedsCapacity,
} from '../../helpers/ssrCurrentBudget';
import {
  addHeaterToSSRService,
  deleteHeaterFromSSRService,
  getAllAdminHeatersService,
} from '../../services/ssrs.service';
import {
  tesHandleChangeSSRDetail,
} from '../store/slices/tesSwitchSlice';
import MessageBox from '../userMessages/messageBox';
import { logger } from '../../helpers/logger';
import { useSwitchState } from '../../hooks/useSwitchState';

const samePartNumber = (a, b) =>
  String(a ?? '')
    .trim()
    .toUpperCase() ===
  String(b ?? '')
    .trim()
    .toUpperCase();

const AdminSSRItemDetails = ({
  isEnable,
  isFault,
  isWarn,
  data,
  handleButtonClick,
  id,
  location,
  machine,
  swtName,
  isMobile = false,
}) => {
  const {
    partNumberSuggestions: partNumberSuggestionsStore,
    elementsOptions: elementsOptionsStore,
  } = useSelector(selectDescription);
  const [partNumberSuggestions, setPartNumberSuggestions] = useState(
    partNumberSuggestionsStore,
  );
  const [elementsOptions, setElementsOptions] = useState(elementsOptionsStore);
  const { specs } = data;

  const { flat: switchData } = useSwitchState(swtName);

  const { deviceMac } = switchData[location][machine];

  const unitsMeasurement = false;

  const {
    mode,
    rows,
    changes,
    hasChanges,
    enterEdit,
    enterAdd,
    cancel,
    setPartNumber,
    removeRow,
  } = useHeaterEditor(specs);

  const [pendingConfirm, setPendingConfirm] = useState(false);
  const [activateMessageBox, setActivateMessageBox] = useState(false);
  const [message, setMessage] = useState(null);
  const dispatch = useDispatch();

  const resolveSpec = (partNumber) => {
    const elementIdx = partNumberSuggestions.findIndex((suggestion) =>
      samePartNumber(suggestion, partNumber),
    );
    return elementIdx >= 0 ? elementsOptions[elementIdx] : null;
  };

  const specFor = (partNumber) =>
    resolveSpec(partNumber) ||
    specs.find((spec) => samePartNumber(spec?.partNumber, partNumber)) ||
    {};

  const buttonsDisabled =
    mode === MODES.VIEW ? ['clear', 'apply'] : ['edit', 'add'];

  const handleButtonPress = (name) => {
    if (name === 'edit') {
      enterEdit();
    } else if (name === 'add') {
      enterAdd();
    } else if (name === 'clear') {
      cancel();
    } else if (name === 'apply') {
      if (rows.some((row) => !row.partNumber.trim())) {
        setMessage(
          'please select a part number for every heater, or remove the row',
        );
        setActivateMessageBox(true);
        return;
      }
      if (!hasChanges) {
        setMessage('no changes to apply');
        setActivateMessageBox(true);
        cancel();
        return;
      }
      const proposedSpecs = rows.map((row) => specFor(row.partNumber));
      if (exceedsCapacity(proposedSpecs)) {
        setMessage(
          `these heaters draw more than the ${SSR_CURRENT_CAPACITY_A}a this SSR can carry`,
        );
        setActivateMessageBox(true);
        return;
      }
      setPendingConfirm(true);
    }
  };

  const handleConfirm = async () => {
    setPendingConfirm(false);

    const addedSpecs = changes.toAdd.map(resolveSpec);
    if (addedSpecs.some((spec) => !spec?.current)) {
      setMessage(
        'element not found! please check again the input specifications',
      );
      setActivateMessageBox(true);
      return;
    }

    const request = { ssr_no: id - 1, deviceType: swtName.toUpperCase() };

    try {
      await Promise.all([
        ...changes.toAdd.map((partNumber) =>
          addHeaterToSSRService(deviceMac, { ...request, partNumber }),
        ),
        ...changes.toDelete.map((partNumber) =>
          deleteHeaterFromSSRService(deviceMac, { ...request, partNumber }),
        ),
      ]);
    } catch (error) {
      setMessage(
        error?.message || 'could not save heater changes, please try again',
      );
      setActivateMessageBox(true);
      cancel();
      return;
    }

    const changeSSRDetail =
      swtName === 'ess' ? handleChangeSSRDetail : tesHandleChangeSSRDetail;
    const nextSpecs = [
      ...removeDeletedSpecs(specs, changes.toDelete),
      ...addedSpecs,
    ];

    dispatch(
      changeSSRDetail({
        location,
        machine,
        data: nextSpecs.length !== 0 ? nextSpecs : [{}],
        id: `ssr${id}`,
        unit: unitsMeasurement,
      }),
    );
    cancel();
    setMessage(null);
    setActivateMessageBox(true);
  };

  useEffect(() => {
    getAllAdminHeatersService()
      .then((res) => {
        setElementsOptions(
          res.map((el) => ({
            elementName: el.elementName,
            partNumber: el.partNumber,
            current: Number(el.current),
            wattage: Number(el.wattage),
            voltage: Number(el.voltage),
            lengths: Number(el.lengths),
          })),
        );
        setPartNumberSuggestions(res.map((el) => el.partNumber));
      })
      .catch((err) => {
        logger.error('fail get admin heaters:', err.message);
      });
  }, []);

  const optionsFor = (partNumber) =>
    partNumber &&
    !partNumberSuggestions.some((s) => samePartNumber(s, partNumber))
      ? [...partNumberSuggestions, partNumber]
      : partNumberSuggestions;

  return (
    <Wrapper isMobile={isMobile}>
      <ContentWrapper
        isEnable={isEnable}
        isFault={isFault}
        isWarn={isWarn}
        isMobile={isMobile}
      >
        {rows.length === 0 && (
          <HeaterRow
            partNumber=''
            spec={{}}
            isLocked
            onChange={() => {}}
            isMobile={isMobile}
          />
        )}
        {rows.map((row, index) => (
          <HeaterRow
            key={index}
            partNumber={row.partNumber}
            spec={specFor(row.partNumber)}
            isEnable={isEnable}
            isLocked={row.locked}
            options={optionsFor(row.partNumber)}
            onChange={(value) => setPartNumber(index, value)}
            onRemove={() => removeRow(index)}
            isMobile={isMobile}
          />
        ))}
      </ContentWrapper>

      <SSRDetailButtonContainer
        handleClick={handleButtonPress}
        buttons={['edit', 'add', 'clear', 'apply']}
        disabled={buttonsDisabled}
        isMobile={isMobile}
      />

      {pendingConfirm && (
        <MessageBoxWrapper isMobile={isMobile}>
          <ConfirmSummary
            changes={changes}
            describeHeater={(partNumber) => {
              const spec = specFor(partNumber);
              return spec?.current
                ? `${partNumber}  ${spec.current}a  ${spec.wattage}w`
                : partNumber;
            }}
            onConfirm={handleConfirm}
            onCancel={() => {
              setPendingConfirm(false);
              cancel();
            }}
          />
        </MessageBoxWrapper>
      )}

      {activateMessageBox && (
        <MessageBoxWrapper isMobile={isMobile}>
          <MessageBox
            onClose={() => {
              setActivateMessageBox(false);
              if (!message) {
                handleButtonClick();
              }
            }}
            title='ssr details settings'
            messages={message ? [message] : ['selections confirmed']}
          />
        </MessageBoxWrapper>
      )}
    </Wrapper>
  );
};
export default AdminSSRItemDetails;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  ${(p) =>
    p.isMobile &&
    css`
      width: 100%;
      align-items: stretch;
      position: relative;
    `}
`;

const ContentWrapper = styled.ul`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  padding: 1.5px 0;
  border-radius: 12px 12px 0 12px;

  ${layerA180Deg};

  ${(p) =>
    p.isEnable ||
    css`
      ${layerADisabled180Deg};
      border-radius: 12px;
    `}

  ${(p) =>
    p.isFault &&
    css`
      border: 1px solid red;
    `};

  ${(p) =>
    p.isWarn &&
    css`
      border: 1px solid #ff7800;
    `};

  ${(p) =>
    p.isMobile &&
    css`
      padding: 4px;
      border-radius: 10px;
    `}
`;

const MessageBoxWrapper = styled.div`
  width: 120%;
  border-radius: 16px;

  display: flex;
  justify-content: center;

  margin-top: 8px;
  padding: 16px;

  position: absolute;
  top: -100px;
  right: 0px;
  z-index: 100;

  cursor: pointer;

  ${(p) =>
    p.isMobile &&
    css`
      width: 100%;
      top: 0;
      right: auto;
      left: 0;
      margin-top: 0;
      padding: 0;
    `}
`;
